import { useEffect, useState } from 'react'
import { useOfflineSync } from '../hooks/useOfflineSync'

const styles = `
  @keyframes slideDown {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes slideUp {
    from {
      transform: translateY(0);
      opacity: 1;
    }
    to {
      transform: translateY(-100%);
      opacity: 0;
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .offline-banner {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 9999;
    background-color: var(--surface);
    border-bottom: 1px solid var(--muted);
    padding: 12px 16px;
    animation: slideDown 0.3s ease-out;
  }

  .offline-banner.exiting {
    animation: slideUp 0.3s ease-in;
  }

  .offline-banner__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 100%;
  }

  .offline-banner__message {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }

  .offline-banner__icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .offline-banner__icon.spinning {
    animation: spin 1s linear infinite;
  }

  .offline-banner__text {
    color: var(--ink);
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .offline-banner__text.muted {
    color: var(--muted);
    font-weight: 400;
  }

  .offline-banner__button {
    flex-shrink: 0;
    margin-left: 12px;
    padding: 6px 12px;
    background-color: var(--accent);
    color: var(--surface);
    border: none;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .offline-banner__button:hover {
    background-color: var(--accent-2);
  }

  .offline-banner__button:active {
    transform: scale(0.98);
  }

  .offline-banner__button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export default function OfflineBanner() {
  const { isOnline, pendingCount, isSyncing, lastSyncTime, syncNow } = useOfflineSync()
  const [isVisible, setIsVisible] = useState(false)
  const [isExiting, setIsExiting] = useState(false)
  const [syncCompleted, setSyncCompleted] = useState(false)

  // Track when sync completes
  useEffect(() => {
    if (!isSyncing && lastSyncTime && isOnline && pendingCount === 0) {
      setSyncCompleted(true)
      const timer = setTimeout(() => {
        setIsExiting(true)
        setTimeout(() => {
          setIsVisible(false)
          setSyncCompleted(false)
          setIsExiting(false)
        }, 300)
      }, 1500)

      return () => clearTimeout(timer)
    }
  }, [isSyncing, lastSyncTime, isOnline, pendingCount])

  // Show/hide banner based on state
  useEffect(() => {
    if (!isOnline || (isOnline && pendingCount > 0)) {
      setIsVisible(true)
      setIsExiting(false)
      setSyncCompleted(false)
    }
  }, [isOnline, pendingCount])

  if (!isVisible) {
    return (
      <>
        <style>{styles}</style>
      </>
    )
  }

  const showButton = isOnline && pendingCount > 0 && !isSyncing

  return (
    <>
      <style>{styles}</style>
      <div className={`offline-banner ${isExiting ? 'exiting' : ''}`}>
        <div className="offline-banner__content">
          <div className="offline-banner__message">
            <div className="offline-banner__icon" style={{ color: isOnline ? 'var(--accent)' : 'var(--muted)' }}>
              {isSyncing ? (
                <svg className="offline-banner__icon spinning" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              ) : syncCompleted ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : !isOnline ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 6v6l4 2" />
                  <circle cx="12" cy="12" r="10" />
                </svg>
              )}
            </div>

            <div className="offline-banner__text">
              {syncCompleted ? (
                'All synced ✓'
              ) : isSyncing ? (
                <>
                  <span>Syncing {pendingCount} item{pendingCount !== 1 ? 's' : ''}...</span>
                </>
              ) : !isOnline ? (
                <>
                  <span>You're offline — </span>
                  <span className="offline-banner__text muted">{pendingCount} item{pendingCount !== 1 ? 's' : ''} pending sync</span>
                </>
              ) : (
                <>
                  <span>{pendingCount} item{pendingCount !== 1 ? 's' : ''} ready to sync</span>
                </>
              )}
            </div>
          </div>

          {showButton && (
            <button className="offline-banner__button" onClick={syncNow} disabled={isSyncing}>
              Sync now
            </button>
          )}
        </div>
      </div>
    </>
  )
}
